import React, { useMemo, useEffect, useState, useRef } from 'react'
import { Button } from 'react-bootstrap'
import Tree from 'react-d3-tree'
import { RawNodeDatum } from 'react-d3-tree/lib/types'
import { TreeNode } from '../../../types/tree'
import { buildRawNodeDatum, nodeWithAllAttributes, refineRawNodeDatum, respondToVisibility } from './helper'
import { ForeignNode, NODE_HEIGHT } from './node'
import { useExport } from '../../../contexts'

type Transform = {
  zoom: number
  x: number
  y: number
}

interface EmulationGraphBaseProps {
  tree: TreeNode
}
export const EmulationGraphBase = (props: EmulationGraphBaseProps): React.ReactElement => {
  const { tree } = props
  const { exporting } = useExport()
  const [transform, setTransform] = useState<Transform>({ zoom: 1, x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const firstRef = useRef<boolean>(true)
  const initRef = useRef<Transform>({ zoom: 0, x: 0, y: 0 })
  const transformRef = useRef<Transform>({ zoom: 0, x: 0, y: 0 })
  const [visible, setVisible] = useState(!exporting)
  const [rects, setRects] = useState<{
    graphRect: DOMRect | null
    containerRect: DOMRect | null
  }>({ graphRect: null, containerRect: null })

  const rect = { width: 160, height: 64, x: -80, y: -32 }
  const nodeSize = { x: 160, y: NODE_HEIGHT }

  const treeData = useMemo<RawNodeDatum>(() => {
    const datum = buildRawNodeDatum(tree, nodeWithAllAttributes)
    if (exporting) {
      refineRawNodeDatum(datum)
    }
    return datum
  }, [tree, exporting])

  useEffect(() => {
    if (containerRef.current) {
      const refValue = containerRef.current
      const observer = respondToVisibility(refValue, (visible: boolean) => {
        setVisible(visible)
      })
      return () => {
        refValue && observer.unobserve(refValue)
      }
    }
  }, [])

  useEffect(() => {
    if (containerRef.current && visible) {
      const graph = containerRef.current.querySelector('.rd3t-g')
      const container = containerRef.current
      if (graph && container) {
        const graphRect = graph.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        setRects({ graphRect, containerRect })
      }
    }
  }, [treeData, visible])

  useEffect(() => {
    const gRect = rects.graphRect
    const cRect = rects.containerRect
    if (gRect?.width && cRect?.width && containerRef.current && firstRef.current) {
      const svg = containerRef.current.querySelector('.rd3t-svg')
      const zoom = transformRef.current.zoom
      let x, y, svgHeight
      if (zoom >= 1) {
        x = (cRect.width - gRect.width) / 2 + (cRect.x - gRect.x)
        y = 10 + (cRect.y - gRect.y)
        svgHeight = gRect.height + 20
      } else {
        x = 10 + (cRect.x - gRect.x) * zoom
        y = 10 + (cRect.y - gRect.y) * zoom
        svgHeight = gRect.height * zoom + 20
      }

      transformRef.current = initRef.current = { zoom, x, y }
      setTransform(transformRef.current)
      svg && svg.setAttribute('style', `height:${svgHeight}px`)

      firstRef.current = false
    }
  }, [rects.containerRect, rects.graphRect])

  const zoomIn = () => {
    const zoom = transformRef.current.zoom * 1.1
    transformRef.current = { ...transformRef.current, zoom }
    setTransform(transformRef.current)
  }

  const zoomOut = () => {
    const zoom = transformRef.current.zoom * 0.9
    transformRef.current = { ...transformRef.current, zoom }
    setTransform(transformRef.current)
  }

  const fitContent = () => {
    transformRef.current = initRef.current
    const orgY = initRef.current.y
    setTransform({ ...transformRef.current, y: transform.y === orgY ? orgY + 0.1 : orgY })
  }

  const onUpdate = (target: any) => {
    transformRef.current = {
      zoom: target.zoom,
      x: target.translate?.x,
      y: target.translate?.y,
    }
  }

  return (
    <div className="graph-container" ref={containerRef}>
      <Tree
        data={treeData}
        nodeSize={nodeSize}
        separation={{ siblings: 1.2, nonSiblings: 1.5 }}
        renderCustomNodeElement={(rd3tProps) => (
          <ForeignNode node={rd3tProps.nodeDatum} path={window.location.pathname} rect={rect} />
        )}
        orientation="vertical"
        svgClassName="svg-container"
        zoom={transform.zoom}
        translate={{ x: transform.x, y: transform.y }}
        onUpdate={onUpdate}
      />
      {!exporting && (
        <div className="zoom-buttons d-flex">
          <Button variant="outline-primary" onClick={zoomIn} size="sm">
            <i className="fas fa-plus" />
          </Button>
          <Button variant="outline-primary" onClick={zoomOut} size="sm">
            <i className="fas fa-minus" />
          </Button>
          <Button variant="outline-primary" onClick={fitContent} size="sm">
            Fit Content
          </Button>
        </div>
      )}
    </div>
  )
}
